import styled, { css } from 'styled-components';
import { Button } from '@lmn/design-system';
import { priceContainerWidthStyle } from '../../utils/styled';
import { PriceAmountGroup, PriceDetailsContainer } from './PriceDetails/styled';

const desktopStyles = css`
  border-left: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  height: 100%;
  padding: 16px;
`;

const overlayDesktop = css`
  width: 100%;
  padding: 0 12px 8px;
  border-left: none;

  &:before {
    content: unset;
  }
`;

const overlayMobile = css`
  margin-top: auto;
  width: auto;
  height: unset;
  padding-top: 0;
`;

const waitingStyles = css`
  flex-direction: column;
  align-items: flex-end;
  & > * {
    margin-top: unset;
  }
`;

const mobileDivider = css`
  &:before {
    content: '';
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
  }
`;

export const PriceContainer = styled.div<{
  isOverlay: boolean;
  isWaiting: boolean;
  isOverlayAndMobile?: boolean;
  hasAgeText?: boolean;
  hasTaxesText?: boolean;
  ref?: React.MutableRefObject<HTMLDivElement | undefined>;
}>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  position: relative;
  padding: 12px 16px 16px;
  ${priceContainerWidthStyle};

  @media (max-width: 767px) {
    ${({ isOverlay }) => isOverlay && overlayMobile};
    ${mobileDivider};
  }

  @media (min-width: 768px) {
    ${desktopStyles};
    ${({ isOverlay }) => isOverlay && overlayDesktop};
  }

  ${({ isOverlay }) => isOverlay && overlayDesktop};
  ${({ isWaiting }) => isWaiting && waitingStyles}
`;

export const PriceCTARow = styled.div<{ isOverlay?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-top: 12px;
  }

  ${({ isOverlay }) =>
    isOverlay &&
    css`
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 8px;

      @media (max-width: 767px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }
    `};
`;

export const PriceInfoWrapper = styled.div<{ isOverlay?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  ${({ isOverlay }) =>
    isOverlay &&
    css`
      align-items: flex-start;
      flex: 1;
      min-width: 0;

      * {
        align-items: flex-start;
      }

      /* The price row is a row flex, where align-items is the vertical alignment of amount and
         label, not the left-alignment the rule above is after. */
      ${PriceDetailsContainer},
      ${PriceAmountGroup} {
        align-items: baseline;
      }
    `};

  @media (max-width: 767px) {
    align-items: flex-start;
    flex: 1;
    /* The overlay keeps its chevron in this row, so the price has to be allowed to shrink: a wrapping
       price row's fit-content is every item on one line, which would push the chevron out of the card. */
    min-width: ${({ isOverlay }) => (isOverlay ? '0' : 'fit-content')};
  }
`;

export const OverlayChevronIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
`;

export const CtaButtonCard = styled(Button)`
  min-width: 140px;
  align-self: flex-end;
  width: 100%;

  @media (min-width: 768px) {
    margin-top: 16px;
  }
`;
